import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton } from '~/components/shadcn/skeleton';
import { Badge } from '~/components/shadcn/badge';
import { Box } from '@nike/podium';
import { ArrowUp, Burn } from '@nike/nike-design-system-icons';
import { getHexColor } from '~/utils/colors';
import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { productImagesAtom } from '~/atoms/productImages';
import { usePreferredImageIndex } from '~/hooks/usePreferredImageIndex';
const THUMBNAIL_COUNT = 4;
function ProductCard({ card, onClick, scale = 'medium' }) {
    const modelName = card.modelName || 'Model Name';
    const franchise = card.franchise || 'Franchise';
    const silo = card.silo || card.silhouetteType || '';
    const silhouette = card.silhouette || 'Silhouette';
    const sport = card.sport || card.sportActivity || 'Sport';
    const gender = card.gender || 'Gender';
    const age = card.age || 'Age';
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const productImagesCache = useAtomValue(productImagesAtom);
    const modelId = ('modelId' in card ? card.modelId : null);
    const cachedImages = modelId
        ? (productImagesCache.get(modelId.toString()) ?? null)
        : null;
    const imagesFetched = cachedImages !== null;
    const imagesToUse = imagesFetched && cachedImages
        ? cachedImages.map((img) => ({
            url: img.url,
            alt: card.modelName || 'Product',
            viewCategory: img.viewCategory
        }))
        : card.images || [];
    const hasImages = Array.isArray(imagesToUse) && imagesToUse.length > 0;
    const hasMultipleImages = hasImages && imagesToUse.length > 1;
    const [selectedImageIndex, setSelectedImageIndex] = usePreferredImageIndex(imagesToUse, [card.id]);
    const primaryImageUrl = hasImages
        ? (imagesToUse?.[selectedImageIndex]?.url ?? null)
        : null;
    const primaryImageAlt = hasImages
        ? (imagesToUse?.[selectedImageIndex]?.alt ?? modelName)
        : modelName;
    const thumbnailImages = hasImages ? imagesToUse.slice(0, THUMBNAIL_COUNT) : [];
    const allPills = [sport, gender, age].filter(Boolean);
    const cardColorways = card.colorways || [];
    const cardTags = card.tags || [];
    const colorwayHexCodes = cardColorways.map(getHexColor);
    const getScaleConfig = () => {
        switch (scale) {
            case 'small':
                return {
                    title: 'text-xs',
                    category: 'text-[10px]',
                    pill: 'text-[10px]',
                    gap: 'gap-1.5',
                    padding: 'px-1.5 pb-4',
                    colorwayGap: 'pt-1.5',
                    iconContainer: 'w-5 h-5',
                    icon: 'w-2.5 h-2.5',
                    thumbnail: 'w-6 h-6',
                    badge: 'text-[10px] min-w-[24px] h-6 px-2',
                    maxPills: 3,
                    maxColorways: 6
                };
            case 'large':
                return {
                    title: 'text-xl',
                    category: 'text-base',
                    pill: 'text-base',
                    gap: 'gap-4',
                    padding: 'px-4 pb-10',
                    colorwayGap: 'pt-4',
                    iconContainer: 'w-8 h-8',
                    icon: 'w-4 h-4',
                    thumbnail: 'w-12 h-12',
                    badge: 'text-sm min-w-[40px] h-10 px-3',
                    maxPills: 6,
                    maxColorways: 10
                };
            case 'medium':
            default:
                return {
                    title: 'text-sm',
                    category: 'text-xs',
                    pill: 'text-xs',
                    gap: 'gap-2',
                    padding: 'px-2 pb-6',
                    colorwayGap: 'pt-2',
                    iconContainer: 'w-6 h-6',
                    icon: 'w-3 h-3',
                    thumbnail: 'w-8 h-8',
                    badge: 'text-xs min-w-[32px] h-8 px-2',
                    maxPills: 4,
                    maxColorways: 8
                };
        }
    };
    const scaleConfig = getScaleConfig();
    return (_jsxs(Box, { className: "w-full p-1 bg-white cursor-pointer rounded-lg border pds-color-border-tertiary hover:shadow-lg flex flex-col", onClick: onClick, role: "button", tabIndex: 0, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                onClick?.();
            }
        }, children: [_jsxs(Box, { className: "w-full aspect-[322/232] rounded flex-shrink-0 relative overflow-hidden flex items-center justify-center", children: [primaryImageUrl && !imageError ? (_jsxs(_Fragment, { children: [imageLoading && (_jsx(Skeleton, { className: "w-full h-full rounded absolute inset-0 z-10" })), _jsx("img", { src: primaryImageUrl, alt: primaryImageAlt, className: "w-full h-full object-cover rounded absolute inset-0", onLoad: () => setImageLoading(false), onError: () => {
                                    setImageError(true);
                                    setImageLoading(false);
                                }, style: {
                                    opacity: imageLoading ? 0 : 1,
                                    transition: 'opacity 0.2s'
                                } })] })) : imagesFetched && !hasImages ? (_jsx("p", { className: "text-sm text-gray-500", children: "No Image Available" })) : (_jsx(Skeleton, { className: "w-full h-full rounded absolute inset-0 bg-gray-200" })), _jsxs(Box, { className: "absolute top-2 left-2 flex gap-2 items-center", children: [_jsx(Box, { className: `${scaleConfig.iconContainer} rounded-full bg-[#707072] flex items-center justify-center`, children: _jsx(ArrowUp, { className: `${scaleConfig.icon} text-white` }) }), _jsx(Box, { className: `${scaleConfig.iconContainer} rounded-full border border-[#111111] flex items-center justify-center`, children: _jsx(Burn, { className: `${scaleConfig.icon} text-[#707072]` }) })] }), _jsxs(Box, { className: "absolute top-2 right-2 flex gap-2 items-center", children: [_jsx(Badge, { variant: "secondary", className: `bg-[rgba(158,158,160,0.2)] text-[#707072] ${scaleConfig.pill} rounded-full px-3`, children: "Alert" }), _jsx(Box, { className: `${scaleConfig.iconContainer} rounded-full border border-[#111111] flex items-center justify-center`, children: _jsxs(Box, { className: "flex flex-col gap-0.5", children: [_jsx(Box, { className: "w-1 h-1 rounded-full bg-[#111111]" }), _jsx(Box, { className: "w-1 h-1 rounded-full bg-[#111111]" }), _jsx(Box, { className: "w-1 h-1 rounded-full bg-[#111111]" })] }) })] }), hasMultipleImages && (_jsx(Box, { className: "absolute bottom-2 left-2 flex items-center gap-2", children: thumbnailImages.map((img, index) => (_jsx(Box, { onClick: (e) => {
                                e.stopPropagation();
                                setSelectedImageIndex(index);
                                setImageLoading(true);
                                setImageError(false);
                            }, className: `${scaleConfig.thumbnail} ${index === selectedImageIndex ? 'ring-2 ring-black' : ''} bg-gray-300 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-gray-400 transition-all`, role: "button", tabIndex: 0, onKeyDown: (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    setSelectedImageIndex(index);
                                }
                            }, children: _jsx("img", { src: img.url, alt: img.alt || `Thumbnail ${index + 1}`, className: "w-full h-full object-cover" }) }, index))) })), hasImages && imagesToUse.length > THUMBNAIL_COUNT && (_jsxs(Box, { className: `absolute bottom-2 right-2 bg-[#CACACB] text-white ${scaleConfig.badge} rounded font-medium flex items-center justify-center`, children: ["+", imagesToUse.length - THUMBNAIL_COUNT] }))] }), _jsxs(Box, { className: `flex flex-col ${scaleConfig.gap} ${scaleConfig.padding}`, children: [_jsx(Box, { className: `w-full flex items-center gap-1.5 flex-wrap ${scaleConfig.colorwayGap}`, children: colorwayHexCodes
                            .slice(0, scaleConfig.maxColorways)
                            .map((color, index) => (_jsx(Box, { className: "w-5 h-5 rounded-full flex-shrink-0", style: { backgroundColor: color }, title: cardColorways[index] || `Colorway ${index + 1}` }, index))) }), _jsx(Box, { className: "w-full flex items-baseline justify-between gap-2", children: _jsx("h3", { className: `${scaleConfig.title} font-medium text-[#111111] dark:text-white line-clamp-1`, children: modelName }) }), _jsxs(Box, { className: "w-full flex items-start justify-between gap-2", children: [_jsx("p", { className: `${scaleConfig.category} text-[#111111] dark:text-white`, children: franchise }), _jsx("p", { className: `${scaleConfig.category} text-[#707072] dark:text-gray-400`, children: silhouette })] }), _jsxs(Box, { className: "w-full flex items-center justify-between gap-2", children: [_jsx("p", { className: `${scaleConfig.category} text-[#707072] dark:text-gray-400`, children: silo }), cardTags.length > 0 && (_jsx(Badge, { variant: "outline", className: "bg-[#F7EBFC] border-0 text-[#111111] hover:bg-[#F7EBFC]", children: cardTags[0] }))] }), _jsxs(Box, { className: "w-full flex gap-2 flex-wrap", children: [allPills.slice(0, scaleConfig.maxPills).map((pill) => (_jsx(Badge, { variant: "secondary", className: `px-3 py-1.5 bg-[rgba(158,158,160,0.2)] text-[#707072] hover:bg-[rgba(158,158,160,0.3)] ${scaleConfig.pill} rounded-full`, children: pill }, pill))), allPills.length > scaleConfig.maxPills && (_jsxs(Badge, { variant: "secondary", className: `px-3 py-1.5 bg-[rgba(158,158,160,0.2)] text-[#707072] hover:bg-[rgba(158,158,160,0.3)] ${scaleConfig.pill} rounded-full`, children: ["+", allPills.length - scaleConfig.maxPills] }))] })] })] }));
}
export default ProductCard;
